<template>
<section class="pb-2">
    <div class="flex justify-between items-center text-xl text-left text-gray-700 dark:text-white/90 font-semibold">
        <section class="flex items-center">
            <n-skeleton v-if="!profile.localization" :width="160" :height="20" :sharp="false" />
            <template v-else>{{ profile.localization.profile_account_info_title }}</template>

            <n-button
                size="small"
                class="ml-4 rounded-2xl"
                @click="goOfficeV1">
                <div class="text-xs font-semibold">office V1</div>
            </n-button>
        </section>

        <n-button
            strong
            size="tiny"
            class="ml-2 rounded-md"
            type="tertiary"
            @click="update"
            :disabled="updateLoading"
            :loading="updateLoading">
            {{ refs.localization.autorefresh.autorefresh_btn }}
        </n-button>
    </div>
    <n-card
        size="small"
        :bordered="false"
        class="overflow-hidden shadow bg-white dark:bg-darkCardBg rounded-lg mt-4"
        :segmented="{
            content: true,
            footer: 'soft'
        }">
        <template #default>
            <section class="flex justify-start mb-4">
                <n-tag v-if="gl.timeToShutdown?.timeToShutdown || gl.timeToShutdown?.timeToStop" type="error" size="small" round>
                    <span class="mr-1">{{ gl.timeToShutdown.status === 2 ? refs.localization.header.stop : refs.localization.header.suspend }}</span>
                    <strong @click="showModal = true" class="underline cursor-pointer hover:no-underline">
                        {{ gl.timeToShutdown.status === 2 ? gl.timeToShutdown?.timeToStop : gl.timeToShutdown?.timeToShutdown }}
                    </strong>
                </n-tag>
            </section>
            <section class="flex">
                <div class="text-left">
                    <n-skeleton v-if="!profile.localization" class="inline-block" :width="50" :height="14" :sharp="false" />
                    <div v-else class="text-gray-500 dark:text-white/40">{{ profile.localization?.profile_account_info_status }}</div>

                    <n-skeleton v-if="loading" class="inline-block mt-1" :width="90" :height="24" :sharp="false" />
                    <div v-else class="text-gray-600 dark:text-white/75 text-base font-semibold mt-1">{{ refs.status }}</div>
                </div>
                <div class="text-left ml-6">
                    <n-skeleton v-if="!profile.localization" class="inline-block" :width="30" :height="14" :sharp="false" />
                    <div v-else class="text-gray-500 dark:text-white/40">{{ profile.localization?.account_info_id }}</div>

                    <n-skeleton v-if="loading" class="inline-block mt-1" :width="20" :height="24" :sharp="false" />
                    <div v-else class="text-gray-600 dark:text-white/75 text-base font-semibold mt-1">{{ env.ID }}</div>
                </div>
                <div class="text-left ml-6">
                    <n-skeleton v-if="!profile.localization" class="inline-block" :width="120" :height="14" :sharp="false" />
                    <div v-else class="text-gray-500 dark:text-white/40">{{ profile.localization?.profile_account_info_balance }}</div>

                    <n-skeleton v-if="loading" class="inline-block mt-1" :width="130" :height="24" :sharp="false" />
                    <div v-else class="flex items-center text-base font-semibold" :class="gl.balance?.balance > 0 ? 'text-green-600' : 'text-red-500'">
                        {{ gl.balance?.balance }}
                        <rb-coin class="w-5 ml-2" :coin="currency" />
                    </div>
                </div>
                <div class="flex-grow flex items-center justify-end">
                    <n-skeleton v-if="!profile.localization" class="inline-block mr-4" :width="160" :height="34" :sharp="false" />
                    <n-button
                        v-else
                        strong
                        class="mr-4 rounded-md text-white/90"
                        :color="gl.mainColor"
                        :disabled="loading"
                        @click="showModal = true">
                        + {{ profile.localization?.profile_account_info_topup_balance_button }}
                    </n-button>

                    <n-skeleton v-if="!profile.localization" class="inline-block mr-2" :width="160" :height="34" :sharp="false" />
                    <n-button
                        v-else
                        secondary
                        type="default"
                        class="mr-2 rounded-md"
                        :disabled="loading"
                        @click="$emit('change-tab', 'account_balance_history')">
                        {{ profile.localization?.profile_account_info_topup_history_button }}
                    </n-button>
                </div>
            </section>
        </template>
        <template #footer>
            <section class="w-full flex justify-between">
                <section class="flex">
                    <n-skeleton v-if="!refs.localization" class="inline-block mr-2" :width="160" :height="34" :sharp="false" />
                    <n-button
                        v-else
                        secondary
                        size="tiny"
                        type="default"
                        class="mr-2 rounded-md"
                        @click="window.open('https://desk.revenuebot.io/en/%D0%B1%D0%B0%D0%B7%D0%B0-%D0%B7%D0%BD%D0%B0%D0%BD%D0%B8%D0%B9', '_blank')">
                        {{ refs.localization.header.knowledge_base }}
                    </n-button>

                    <n-skeleton v-if="!refs.localization" class="inline-block mr-2" :width="160" :height="34" :sharp="false" />
                    <n-button
                        v-else
                        secondary
                        size="tiny"
                        type="default"
                        class="mr-2 rounded-md"
                        @click="window.open('https://appt.link/consultation_from_support/revenuebot', '_blank')">
                        {{ refs.localization.header.free_consultations }}
                    </n-button>

                    <n-skeleton v-if="!refs.localization" class="inline-block mr-2" :width="160" :height="34" :sharp="false" />
                    <n-button
                        v-else
                        secondary
                        size="tiny"
                        type="default"
                        class="mr-2 rounded-md"
                        @click="window.open('https://www.youtube.com/channel/UCrAyFAxvDzPIwYKgdSDwCFg', '_blank')">
                        {{ refs.localization.header.educational_videos }}
                    </n-button>
                </section>
                <n-skeleton v-if="!refs.localization" class="inline-block mr-2" :width="160" :height="34" :sharp="false" />
                <n-button
                    v-else
                    secondary
                    size="tiny"
                    type="default"
                    class="rounded-md"
                    @click="window.open(refs.lang === 'ru'
                            ? 'https://t.me/revenuebot_user'
                            : 'https://t.me/revenuebot_users_en', '_blank')">
                    <template #icon>
                        <telegram-icon class="fill-[#1A6DE0]" />
                    </template>
                    UsersChat
                </n-button>
            </section>
        </template>
    </n-card>
    <main-info class="mt-6" />
</section>

<topup-balance
    v-model:show="showModal" />
</template>

<script>
// vue
import { h, ref, watch, computed } from 'vue';

// store
import { useGl } from '@store/ts/gl';
import { useEnv } from '@store/ts/env';
import { useRefs } from '@store/ts/refs';
import { useApiKeys } from '@store/apiKeys';
import { useProfile } from '@store/profile';

// i18n
import { useI18n } from 'vue-i18n';

// components
import RbCoin from '@components/rb-coin';
import TopupBalance from '@components/topup-balance';
import TelegramIcon from '@components/icons/telegram.vue';
import MainInfo from '@components/profile/main-info/desktop.vue';

// services
import Api from '@services/api';

// icons
import {
    Star12Filled,
    Copy16Regular,
    Dismiss16Filled,
    IosArrowRtl24Filled } from '@vicons/fluent';

// naive-ui
import {
    NTag,
    NIcon,
    NCard,
    NModal,
    NAlert,
    NSelect,
    NButton,
    NCollapseTransition,
    useMessage } from 'naive-ui';

export default {
    name: 'account-info',
    components: {
        NTag,
        NIcon,
        NCard,
        NAlert,
        NModal,
        RbCoin,
        NButton,
        NSelect,
        MainInfo,
        TopupBalance,
        Star12Filled,
        TelegramIcon,
        Copy16Regular,
        Dismiss16Filled,
        NCollapseTransition,
        IosArrowRtl24Filled,
    },
    setup() {
        // store
        const gl = useGl();
        const env = useEnv();
        const refs = useRefs();
        const profile = useProfile();

        // i18n
        const { t } = useI18n();

        // vars
        const show = ref(true);
        const showModal = ref(false);
        const selectionCurrency = ref();
        const updateLoading = ref(false);

        watch(showModal, () => {
            setTimeout(() => {
                selectionCurrency.value = null;
            }, 300);
        });

        const currency = computed(() => refs.currencies.find(el => el.id === env.profitSymbol));
        const topupSelectMethods = computed(() => {
            if (!Array.isArray(env.topupMethods)) return [];

            return env.topupMethods.map(cur => {
                const curEntry = refs.currencies.find(({ id }) => id === cur.currency);
                if (curEntry) {
                    cur.label = cur.title;
                    cur.value = cur.id;

                    return {...curEntry, ...cur};
                }
                return null;
            }).filter(cur => cur);
        });


        const renderLabel = option => {
            return h(
                'div',
                {
                    style: {
                        display: 'flex',
                        alignItems: 'center',
                    },
                },
                [
                    h(RbCoin, {
                        class: 'my-1',
                        coin: option,
                    }),
                    h(
                        'div',
                        {
                            style: {
                                marginLeft: '12px',
                                padding: '4px 0',
                            },
                        },
                        [
                            h('div', null, [option.label]),
                        ],
                    ),
                ],
            );
        };
        const setSelectionCurrency = e => {
            selectionCurrency.value = topupSelectMethods.value.find(({ id }) => id === e);
        };

        const copy = s => {
            navigator.clipboard.writeText(s);
            gl.showNotification({
                type: 'success',
                msg: t('copied'),
            });
        };

        const update = async () => {
            updateLoading.value = true;

            await Api.getEnv();

            updateLoading.value = false;
        };

        const getCurrency = icon => refs.currencies.find(el => el.id === icon);

        const goOfficeV1 = () => {
            let url = new URL(window.location.href);
            window.open(url.origin + '/officev1/', '_blank');
        };

        return {
            gl,
            env,
            refs,
            show,
            window,
            profile,
            currency,
            showModal,
            updateLoading,
            selectionCurrency,
            t,
            copy,
            update,
            getCurrency,
            renderLabel,
            topupSelectMethods,
            setSelectionCurrency,
            goOfficeV1,
        };
    },
};
</script>